import { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AppButton } from "@libs/zapi-ui/custom";
import { ChatShell } from "@/zapi-frontend/shared/ui/chat";
import { usePresentationChat, usePresentationChatStore } from "../hooks/usePresentationChat";
import { usePresentation } from "@/zapi-frontend/shared/hooks/usePresentation";
import { useOutlineChangeStatus, dismissChanges } from "../hooks/useOutlineChangeStatus";
import { getNotificationConfig } from "../const/consts";
import { TActionButton } from "../model/types";
import { PresentationChatMessageList } from "./PresentationChatMessageList";

type TPresentationChatProps = {
  selectedSlideIndex: number;
};

export function PresentationChat({ selectedSlideIndex }: TPresentationChatProps) {
  const { id: projectId } = useParams() as { id: string };
  const {
    messages,
    hasMessages,
    isLoading,
    isSendingRequest,
    isAbortingRequest,
    loadingMessageId,
    handleSubmit,
    handleAbortRequest,
    handleClearChat,
    handleActionButton
  } = usePresentationChat({ selectedSlideIndex });

  const { t } = useTranslation("projectHome");
  const { addMessage, markMessageActedOn, removeMessage } = usePresentationChatStore();
  const changesDismissedAt = usePresentationChatStore((s) => s.changesDismissedAt[projectId] || null);

  const { outlineGeneratedAt, hasSlides, isSlidesGenerating } = usePresentation({ enabled: false });
  const outlineStatus = useOutlineChangeStatus(outlineGeneratedAt, hasSlides);

  useEffect(() => {
    const config = getNotificationConfig(t)[outlineStatus];
    const currentMessages = usePresentationChatStore.getState().getChatHistory(projectId);

    const existingUnacted = currentMessages.find((m) => m.id.startsWith("notification-") && !m.isActedOn);

    if (!config || !outlineGeneratedAt) {
      if (existingUnacted) removeMessage(projectId, existingUnacted.id);
      return;
    }

    const newId = `notification-${outlineStatus}-${outlineGeneratedAt}-${changesDismissedAt || "initial"}`;

    if (currentMessages.some((m) => m.id === newId)) return;

    if (existingUnacted) {
      removeMessage(projectId, existingUnacted.id);
    }

    addMessage(projectId, {
      role: "assistant",
      id: newId,
      content: JSON.stringify({
        type: "notification",
        content: config.content,
        changes: config.changes,
        question: config.question,
        actionButtons: config.actionButtons
      })
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outlineStatus, outlineGeneratedAt, projectId, changesDismissedAt]);

  const onActionButton = (messageText: string, messageId: string) => {
    const message = messages.find((m) => m.id === messageId);
    if (message) {
      try {
        const parsed = JSON.parse(message.content);
        const btn = parsed.actionButtons?.find((b: TActionButton) => b.message === messageText);
        if (btn?.action === "dismiss") {
          dismissChanges(projectId);

          markMessageActedOn(projectId, messageId);
          const ts = Date.now();
          addMessage(projectId, { role: "user", id: `user-${ts}`, content: messageText });
          if (btn.responseMessage) {
            addMessage(projectId, {
              role: "assistant",
              id: `bot-${ts}`,
              content: JSON.stringify({ type: "notification", content: btn.responseMessage })
            });
          }
          return;
        }
      } catch {
        // not a notification message, fall through to submit
      }
    }
    dismissChanges(projectId);
    handleActionButton(messageText, messageId);
  };

  const displayedMessages = useMemo(
    () => (hasSlides ? messages : messages.filter((m) => !m.id.startsWith("notification-"))),
    [messages, hasSlides]
  );

  const [input, setInput] = useState("");

  const submit = () => {
    const messageText = input.trim();
    if (!messageText || isLoading || isSendingRequest || !hasSlides) return;
    setInput("");
    handleSubmit(messageText);
  };

  return (
    <ChatShell
      data-testid="presentation-chat-container"
      className="h-full w-full"
      bodyClassName="p-0 overflow-hidden"
      placeholder={t("presentation.chatPlaceholder")}
      input={input}
      // The presentation form treated NBSP as a newline (dictation input); keep that quirk.
      onInputChange={(value) => setInput(value.replace(/\u00a0/g, "\n"))}
      onSubmit={submit}
      isBusy={isLoading || isSendingRequest || !hasSlides}
      controls={
        isLoading && hasSlides && !isSendingRequest ? (
          <AppButton
            color="red400"
            size="sm"
            type="button"
            onClick={handleAbortRequest}
            data-testid="presentation-abort-button"
            isLoading={isAbortingRequest}
          >
            {t("presentation.cancel")}
          </AppButton>
        ) : undefined
      }
      onClear={handleClearChat}
      canClear={hasMessages && !isLoading && hasSlides}
    >
      <PresentationChatMessageList
        messages={displayedMessages}
        loadingMessageId={loadingMessageId}
        onActionButton={onActionButton}
        isSlidesGenerating={isSlidesGenerating}
        hasSlides={hasSlides}
      />
    </ChatShell>
  );
}
